import React, {useMemo} from "react";
import {ListboxButton} from "headlessui-v2";
import {twJoin} from "tailwind-merge";
import {ProductSelectionOption} from "@/data/entities/cart";
import {FormattedMessage, useIntl} from "react-intl";
import {useFulfillmentType} from "@widgets/pos/order/hooks/UseFulfillmentType";
import useProductInfo from "../../context/ProductInfoProvider";
import {formatMoney, getProductPrice} from "@/utils/MoneyUtils";
import {ChevronDownIcon} from "@heroicons/react/16/solid";

type SummarizedItems = {
    names: string[],
    totalPrice: number,
};

type Props = {
    selected: ProductSelectionOption[],
};

const ProductOptionButton = ({selected}: Props) => {
    const intl = useIntl();
    const fulfillmentType = useFulfillmentType();
    const {canAddToBasket} = useProductInfo();
    const summarizedItems: SummarizedItems = useMemo(() => {
        const result: SummarizedItems = {names: [], totalPrice: 0};
        selected.forEach(item => {
            result.names.push(item.optionName);
            result.totalPrice += Number(getProductPrice(item, fulfillmentType))
        })
        return result;
    }, [selected, fulfillmentType]);
    const formattedName: string = useMemo(() => summarizedItems.names.join(", "), [summarizedItems]);

    return (
        <ListboxButton
            disabled={!canAddToBasket}
            className={twJoin(
                "group/btn relative pl-3 pr-9 py-2 rounded-md text-left w-full",
                "transition-colors duration-150 border",
                "bg-white data-[hover]:bg-gray-100 data-[open]:bg-gray-200  border-gray-200",
                "dark:bg-slate-700 dark:border-slate-600",
                "outline-0 focus-[visible]:ring-2 ring-inset ring-blue-600",
                "data-[disabled]:bg-gray-200 dark:data-[disabled]:bg-slate-600 data-[disabled]:cursor-not-allowed"
            )}
            title={intl.formatMessage({id: "picked-item-names"}, {items: formattedName})}
        >
            <div className={"w-full flex flex-row gap-3 items-center justify-between"}>
                <strong
                    className={twJoin(
                        summarizedItems.names.length === 0
                            ? "text-gray-500 dark:text-gray-300"
                            : "text-black dark:text-white",
                        "font-medium group-data-[disabled]/item:text-gray-500 group-data-[disabled]/btn:text-gray-400 whitespace-nowrap truncate",
                    )}
                >
                    {
                        summarizedItems.names.length === 0
                            ? <FormattedMessage id={"choose"}/>
                            : formattedName
                    }
                </strong>

                <data
                    className={twJoin(
                        "font-medium whitespace-nowrap",
                        summarizedItems.names.length === 0
                            ? "invisible"
                            : "",
                        summarizedItems.totalPrice === 0
                            ? "text-green-700 dark:text-green-400"
                            : "text-gray-500 dark:text-gray-200",
                        "group-data-[disabled]/btn:text-gray-400 dark:group-data-[disabled]/btn:text-gray-300",
                    )}
                    value={summarizedItems.totalPrice}
                >
                    {summarizedItems.totalPrice === 0
                        ? <FormattedMessage id={"free"}/>
                        : <FormattedMessage
                            id={summarizedItems.totalPrice >= 0 ? "plus-price" : "minus-price"}
                            values={{price: formatMoney(summarizedItems.totalPrice)}}
                        />
                    }
                </data>
            </div>

            <ChevronDownIcon
                className={"transition-transform absolute right-3 top-1/2 -translate-y-1/2 size-4 group-data-[open]/btn:rotate-180 opacity-50 group-data-[disabled]/btn:opacity-20"}
            />
        </ListboxButton>

    );
};

export default ProductOptionButton;